import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'

/**
 * AI Engineer — one of the five open roles the Careers page lists.
 *
 * The page shipped short on 2026-09-07 with a `check` saying the description
 * was still coming; it arrived the same day and this is it, set in the site's
 * section vocabulary the way `careers-growth-engineer.ts` is: the mission on
 * the grey band, the success measures as a list, the work and the offer as
 * full-width rows. The emoji headings of the source document are dropped — the
 * rest of the site sets its section headings as type.
 *
 * "How we like to work" is kept as its own band rather than folded into the
 * fit list: the line about the development team already being 100% agentic is
 * the sentence a candidate will decide on, and it is a statement about
 * Digitopia rather than about the applicant.
 *
 * Every "Apply now" points at `#contact`, the shared contact band's own anchor
 * at the foot of the document.
 */
export const route: RouteDef = {
  id: 'careersAiEngineer',
  path: 'become-a-digitopian/ai-engineer',
  priority: 0.6,
}

export const careersAiEngineer: WrittenPage = {
  navLabel: 'AI Engineer',
  title: 'AI Engineer',
  description:
    'Open role at Digitopia: AI Engineer, hybrid in Istanbul. A strong software engineer who can turn fast-moving AI technology into dependable products — agents, evals, guardrails and platform services.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Careers',
      headline: 'AI Engineer',
      sub: 'Location: Hybrid (Istanbul) — Full-time',
      body: [
        'A strong software engineer who can turn fast-moving AI technology into dependable products and capabilities. You will work across experimentation and production, building the foundations that power AI experiences across Digitopia’s products and solutions.',
      ],
      ctas: [{ label: 'Apply now', href: '#apply' }],
    },
    {
      kind: 'prose',
      heading: 'We’re on a mission',
      paragraphs: [
        'Digitopia is reinventing consulting. We help ambitious organizations #ScaleValue. As creators of the Impact Platform and maturity frameworks, we help companies understand where they stand, decide what matters next, and turn their digital and AI ambitions into measurable progress.',
        'Now, we are expanding the AI capabilities behind that mission. We are building reusable agents, intelligent workflows and reliable platform services that help organizations move from insight to action.',
        'To make that happen, we are looking for an AI Engineer: a strong software engineer who can turn fast-moving AI technology into dependable products and capabilities.',
      ],
    },
    {
      kind: 'list',
      heading: 'What success looks like',
      intro:
        'You will help make AI useful, reliable and scalable across Digitopia. Success means:',
      items: [
        'Shipping production-ready AI capabilities that solve meaningful user and business problems.',
        'Turning promising prototypes into reliable and maintainable services.',
        'Improving evaluation, observability, safety, speed and cost as our AI systems scale.',
        'Creating reusable foundations that help product and customer-facing teams move faster.',
      ],
    },
    {
      kind: 'features',
      variant: 'rows',
      heading: 'What you’d work on',
      items: [
        {
          title: 'Evals',
          body: 'Building practical ways to measure whether an AI system is actually performing better, rather than relying on subjective feedback.',
        },
        {
          title: 'Guardrails',
          body: 'Designing and implementing safeguards that help AI systems behave reliably and appropriately.',
        },
        {
          title: 'Observability',
          body: 'Making it easy to understand what happened during every AI interaction, including failures and unexpected behavior.',
        },
        {
          title: 'Real tool use',
          body: 'Enabling agents to access the tools, services and information they need while completing a task.',
        },
        {
          title: 'Retrieval',
          body: 'Choosing the right way to access information, whether through graph queries, embeddings, search or simple lookups.',
        },
        {
          title: 'Cost and speed',
          body: 'Improving performance through techniques such as prompt caching, model tiering and efficient context management.',
        },
        {
          title: 'An agent-friendly platform',
          body: 'Building platform capabilities such as service accounts, batch operations, reliable endpoints and server-managed state so agents can work effectively and safely.',
        },
      ],
    },
    {
      kind: 'list',
      heading: 'What makes you a great fit',
      items: [
        'Solid software engineering. Strong fundamentals in APIs, testing, data modeling and system design. This is the part we care about most.',
        'A willingness to be in the system, not beside it. You will contribute to architecture, code review, documentation, CI/CD and security, because our AI capabilities are part of a real production platform. This is not a role focused only on writing prompts — we are looking for someone who wants to take ownership of the engineering behind AI products as well.',
        'Something you’ve built with an LLM that went past the demo stage — a repository, a deployed application, a technical project or a write-up. We would genuinely like to see what you have built.',
        'Comfort weighing model quality against user experience, technical complexity, speed and cost rather than optimizing only one of them.',
        'Curiosity that stays strong even as the field continues to change.',
        'Enjoying the company of consultants and domain experts — many of our best product and design ideas come from people who deeply understand the business problem, even if they do not write code.',
        'Strong new graduates are very welcome, especially those with internships, research, open-source work or personal projects that demonstrate the same engineering mindset.',
      ],
    },
    {
      kind: 'list',
      heading: 'Technical skills we’re looking for',
      items: [
        'Cloud and AI platforms: AWS, AWS Bedrock, and Anthropic’s Claude.',
        'AI patterns: agentic AI, RAG, vector databases, prompt caching, evaluations and guardrails.',
        'Backend and integration: Kotlin, Java, Spring, gRPC, OpenAPI/Swagger, PostgreSQL, SAP or SAP BTP, and enterprise system integration.',
        'Data and infrastructure: Databricks, Snowflake, Docker, Kubernetes, Terraform, and CI/CD.',
      ],
      body: 'You do not need to match every keyword. We care more about what you can build, how rigorously you think, how quickly you learn, and how seriously you take ownership.',
    },
    {
      kind: 'list',
      heading: 'How we like to work',
      items: [
        'We believe in using the simplest mechanism that solves the problem well — embeddings instead of a chat model, a simple lookup instead of retrieval, stateless subagents instead of shared context.',
        'We value solutions that are easy to test, understand and maintain.',
        'Our development team is already 100% agentic: AI agents write software, while engineers direct, review and take responsibility for the output.',
        'Understanding the platform deeply, reviewing AI-generated output critically, and taking responsibility for what gets shipped are important parts of the role.',
      ],
    },
    {
      kind: 'features',
      variant: 'rows',
      heading: 'What’s in it for you',
      items: [
        {
          title: 'Build the AI core',
          body: 'Shape capabilities that can be reused across Digitopia’s platform, consulting solutions and customer engagements.',
        },
        {
          title: 'Modern AI exposure',
          body: 'Work with enterprise AI patterns, AWS Bedrock, Anthropic’s Claude, and the latest developments in generative and agentic AI.',
        },
        {
          title: 'End-to-end ownership',
          body: 'Move from exploration and architecture to deployment, measurement and continuous improvement.',
        },
        {
          title: 'Continuous growth',
          body: 'Access mentorship, courses, experimentation time and internal knowledge-sharing.',
        },
        {
          title: 'Real impact',
          body: 'See your work help organizations make better decisions and execute transformation more effectively.',
        },
        {
          title: 'Balanced flexibility',
          body: 'Enjoy hybrid working, flexible hours and generous paid time off.',
        },
        {
          title: 'Supportive culture',
          body: 'Join a team that values authenticity, curiosity, bold ideas and learning through action.',
        },
        {
          title: 'Long-term rewards',
          body: 'Receive a highly competitive compensation package with stock options tied to your lasting impact.',
        },
      ],
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Ready to build AI capabilities that move beyond demos and create lasting enterprise value?',
      sub: 'Apply now. We would love to meet you if you are ready to experiment, engineer, and own the outcome.',
      cta: { label: 'Apply now', href: '#apply' },
    },
    {
      kind: 'apply',
      opening: 'ai-engineer',
      heading: 'Apply for AI Engineer',
      sub: 'Hybrid (Istanbul), full-time. Tell us who you are and attach your CV; we read every application and reply either way.',
    },
  ],
}
